// Компонент онбординга студента: трёхшаговый опрос (возраст, уровень, цель), сохраняет профиль на сервере.

import { useState } from 'react';
import { Container, Card, Button, Form, Alert, ProgressBar } from 'react-bootstrap';
import { saveStudentProfile } from '../../api/studentApi';

const STEPS = [
  { id: 'age', label: 'Ваш возраст' },
  { id: 'level', label: 'Уровень английского' },
  { id: 'goal', label: 'Цель изучения' },
];

const AGE_OPTIONS = [
  { value: '10-17', label: '10–17 лет' },
  { value: '18-25', label: '18–25 лет' },
  { value: '26-40', label: '26–40 лет' },
  { value: '40+', label: 'Старше 40' },
];

const LEVEL_OPTIONS = [
  { value: 'A1', label: 'A1 — Beginner', desc: 'Только начинаю знакомство с английским' },
  { value: 'A2', label: 'A2 — Elementary', desc: 'Знаю базовые слова и фразы' },
  { value: 'B1', label: 'B1 — Pre-Intermediate', desc: 'Могу общаться на знакомые темы' },
  { value: 'B2', label: 'B2 — Intermediate', desc: 'Свободно общаюсь на большинство тем' },
  { value: 'C1', label: 'C1 — Advanced', desc: 'Владею языком на высоком уровне' },
];

const GOAL_OPTIONS = [
  { value: 'travel', label: 'Для путешествий', icon: '✈️' },
  { value: 'work', label: 'Для работы', icon: '💼' },
  { value: 'exam', label: 'Для учёбы / экзамена', icon: '📝' },
  { value: 'general', label: 'Общий интерес', icon: '🌍' },
];

export default function StudentOnboarding({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ age: '', level: '', goal: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentStep = STEPS[step];
  const progress = Math.round(((step) / STEPS.length) * 100);
  const isLast = step === STEPS.length - 1;

  const canNext = answers[currentStep.id] !== '';

  const handleNext = async () => {
    if (!canNext) return;
    if (isLast) {
      setLoading(true);
      setError('');
      try {
        await saveStudentProfile({
          age_range: answers.age,
          english_level: answers.level,
          learning_goal: answers.goal,
        });
        onComplete();
      } catch {
        setError('Не удалось сохранить профиль. Попробуйте ещё раз.');
      } finally {
        setLoading(false);
      }
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => setStep((s) => s - 1);

  const select = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));

  return (
    <div
      className="ewl-auth-page"
      style={{ background: 'var(--ewl-bg-body)', minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Container>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          {/* Логотип и заголовок */}
          <div className="text-center mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: '3.5rem',
                height: '3.5rem',
                borderRadius: '1rem',
                background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                fontSize: '1.5rem',
                boxShadow: '0 4px 16px rgba(79,110,247,0.35)',
              }}
            >
              📖
            </div>
            <h4 className="fw-bold mb-1" style={{ letterSpacing: '-0.01em' }}>
              Добро пожаловать!
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem', maxWidth: 360, margin: '0 auto' }}>
              Ответьте на 3 вопроса — и мы подберём подходящие упражнения
            </p>
          </div>

          <Card
            className="border-0"
            style={{ borderRadius: '1rem', boxShadow: '0 4px 24px rgba(0,0,0,0.09)' }}
          >
            <Card.Body className="p-4">
              {/* Прогресс */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold" style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
                    Шаг {step + 1} из {STEPS.length}
                  </span>
                  <span className="fw-semibold" style={{ fontSize: '0.8125rem', color: 'var(--bs-primary)' }}>
                    {progress}%
                  </span>
                </div>
                <ProgressBar
                  now={progress}
                  style={{ height: '0.375rem', borderRadius: '1rem' }}
                  variant="primary"
                />
              </div>

              {/* Заголовок шага */}
              <h5 className="fw-bold mb-4" style={{ color: '#1e2a4a' }}>
                {currentStep.label}
              </h5>

              {error && (
                <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem', borderRadius: '0.5rem' }}>
                  {error}
                </Alert>
              )}

              {/* Контент шага */}
              {step === 0 && (
                <div className="d-flex flex-column gap-2">
                  {AGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => select('age', opt.value)}
                      className="text-start"
                      style={{
                        background: answers.age === opt.value
                          ? 'rgba(79,110,247,0.08)'
                          : '#f8f9fc',
                        border: answers.age === opt.value
                          ? '2px solid var(--bs-primary)'
                          : '2px solid transparent',
                        borderRadius: '0.625rem',
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        fontWeight: answers.age === opt.value ? 600 : 500,
                        color: answers.age === opt.value ? 'var(--bs-primary)' : '#212529',
                        transition: 'all 0.15s ease',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="d-flex flex-column gap-2">
                  {LEVEL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => select('level', opt.value)}
                      className="text-start"
                      style={{
                        background: answers.level === opt.value
                          ? 'rgba(79,110,247,0.08)'
                          : '#f8f9fc',
                        border: answers.level === opt.value
                          ? '2px solid var(--bs-primary)'
                          : '2px solid transparent',
                        borderRadius: '0.625rem',
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div
                        className="fw-semibold"
                        style={{
                          color: answers.level === opt.value ? 'var(--bs-primary)' : '#1e2a4a',
                          fontSize: '0.9375rem',
                        }}
                      >
                        {opt.label}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.15rem' }}>
                        {opt.desc}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {step === 2 && (
                <div className="d-flex flex-column gap-2">
                  {GOAL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => select('goal', opt.value)}
                      className="text-start d-flex align-items-center gap-3"
                      style={{
                        background: answers.goal === opt.value
                          ? 'rgba(79,110,247,0.08)'
                          : '#f8f9fc',
                        border: answers.goal === opt.value
                          ? '2px solid var(--bs-primary)'
                          : '2px solid transparent',
                        borderRadius: '0.625rem',
                        padding: '0.875rem 1rem',
                        cursor: 'pointer',
                        fontWeight: answers.goal === opt.value ? 600 : 500,
                        color: answers.goal === opt.value ? 'var(--bs-primary)' : '#212529',
                        transition: 'all 0.15s ease',
                        fontSize: '0.9375rem',
                      }}
                    >
                      <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Навигация */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                <div>
                  {step > 0 ? (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleBack}
                      style={{ borderRadius: '0.5rem', fontWeight: 500 }}
                    >
                      Назад
                    </Button>
                  ) : (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={onSkip}
                      style={{ color: '#6c757d', textDecoration: 'none', fontWeight: 500, padding: 0 }}
                    >
                      Пропустить
                    </Button>
                  )}
                </div>

                <Button
                  onClick={handleNext}
                  disabled={!canNext || loading}
                  style={{
                    background: canNext
                      ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)'
                      : '#e9ecef',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    minWidth: 140,
                    color: canNext ? '#fff' : '#adb5bd',
                  }}
                >
                  {loading
                    ? 'Сохраняем...'
                    : isLast
                    ? 'Начать заниматься'
                    : 'Далее'}
                </Button>
              </div>
            </Card.Body>
          </Card>

          <p className="text-center mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
            Вы сможете изменить эти настройки в профиле позже
          </p>
        </div>
      </Container>
    </div>
  );
}
